import { useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowLeft, Check, MessageCircle, Phone, ArrowRight } from "lucide-react";
import { PRODUCTS, COMPANY } from "../data/site";

export default function ProductDetail() {
  const { slug } = useParams();
  const product = PRODUCTS.find((p) => p.slug === slug);
  const gallery = product?.images && product.images.length > 0 ? product.images : (product ? [product.image] : []);
  const [activeImg, setActiveImg] = useState(0);
  if (!product) return <Navigate to="/products" replace />;

  const related = PRODUCTS.filter((p) => p.slug !== slug).slice(0, 3);

  return (
    <div data-testid="product-detail-page">
      <section className="py-12 md:py-16 border-b border-[#E2E8F0]">
        <div className="max-w-7xl mx-auto px-4 lg:px-8">
          <Link to="/products" className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.18em] text-[#475569] hover:text-[#0EA5E9]" data-testid="back-to-products">
            <ArrowLeft size={14} /> All Products
          </Link>

          <div className="mt-8 grid lg:grid-cols-12 gap-10">
            <div className="lg:col-span-7">
              <div className="aspect-[4/3] bg-[#F1F5F9] overflow-hidden">
                <img
                  src={gallery[activeImg]}
                  alt={product.name}
                  className="w-full h-full object-cover"
                  loading="eager"
                />
              </div>
              {gallery.length > 1 && (
                <div className="mt-3 grid grid-cols-5 sm:grid-cols-6 gap-2" data-testid="product-thumbnails">
                  {gallery.map((src, i) => (
                    <button
                      key={src}
                      type="button"
                      onClick={() => setActiveImg(i)}
                      className={`aspect-square overflow-hidden bg-[#F1F5F9] border-2 transition-colors ${
                        i === activeImg ? "border-[#0EA5E9]" : "border-transparent hover:border-[#94A3B8]"
                      }`}
                      data-testid={`thumb-${i}`}
                      aria-label={`Show image ${i + 1}`}
                    >
                      <img src={src} alt="" loading="lazy" className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              )}
              <div className="mt-3 flex flex-wrap gap-2">
                {product.tags.map((t) => (
                  <span key={t} className="text-[10px] uppercase tracking-wider bg-[#0A192F] text-white px-3 py-1.5">
                    {t}
                  </span>
                ))}
              </div>
            </div>

            <div className="lg:col-span-5">
              <div className="text-xs font-bold uppercase tracking-[0.25em] text-[#0EA5E9] mb-3">
                Industrial Spare
              </div>
              <h1 className="font-head font-black text-[#0A192F] text-4xl md:text-5xl leading-[1.05] tracking-tight">
                {product.name}
              </h1>
              <p className="mt-5 text-[#475569] text-lg leading-relaxed">
                {product.description}
              </p>

              <div className="mt-8">
                <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#0A192F] mb-3">
                  Specifications
                </div>
                <ul className="space-y-2">
                  {product.specs.map((s) => (
                    <li key={s} className="flex items-start gap-2 text-[#475569]">
                      <Check size={16} className="text-[#0EA5E9] mt-1 shrink-0" />
                      <span>{s}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Link
                  to={`/enquiry?product=${encodeURIComponent(product.name)}`}
                  className="bg-[#0EA5E9] hover:bg-[#0284C7] text-white px-5 py-4 font-bold text-xs uppercase tracking-[0.18em] inline-flex items-center justify-center gap-2"
                  data-testid="product-enquiry-cta"
                >
                  Send Enquiry <ArrowRight size={14} />
                </Link>
                <a
                  href={`${COMPANY.whatsapp}?text=${encodeURIComponent(`Hi, I'm interested in ${product.name}.`)}`}
                  target="_blank"
                  rel="noreferrer"
                  className="bg-[#25D366] hover:bg-[#1ebe57] text-white px-5 py-4 font-bold text-xs uppercase tracking-[0.18em] inline-flex items-center justify-center gap-2"
                  data-testid="product-whatsapp-cta"
                >
                  <MessageCircle size={14} /> WhatsApp
                </a>
                <a
                  href={COMPANY.phoneHref}
                  className="sm:col-span-2 border-2 border-[#0A192F] text-[#0A192F] hover:bg-[#0A192F] hover:text-white transition-colors px-5 py-4 font-bold text-xs uppercase tracking-[0.18em] inline-flex items-center justify-center gap-2"
                  data-testid="product-call-cta"
                >
                  <Phone size={14} /> Call {COMPANY.phone}
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-[#F8FAFC]">
        <div className="max-w-7xl mx-auto px-4 lg:px-8">
          <h3 className="font-head font-black text-[#0A192F] text-2xl md:text-3xl tracking-tight mb-8">
            Related Products
          </h3>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {related.map((p, i) => (
              <Link key={p.slug} to={`/products/${p.slug}`} className="group block bg-white border border-[#E2E8F0] hover:border-[#0EA5E9] transition-colors" data-testid={`related-${p.slug}`}>
                <div className="aspect-[4/3] overflow-hidden bg-[#F1F5F9]">
                  <img src={p.image} alt={p.name} loading="lazy" className="zoom-img w-full h-full object-cover" />
                </div>
                <div className="p-5">
                  <h4 className="font-head font-bold text-[#0A192F]">{p.name}</h4>
                  <p className="text-sm text-[#475569] mt-1 line-clamp-2">{p.short}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
